// MassPay's ID check (Veriff, run by MassPay) — opening it and noticing the
// talent is back. Same rule as Stripe (lib/stripeFlow): on native it opens in
// the IN-APP browser sheet, so the app stays underneath and never loses the
// talent to Safari. On the web it opens in a new tab, so this tab — the app —
// is still here when they come back (Veriff's last page belongs to MassPay
// and has no way back to us).
import { Browser } from "@capacitor/browser";
import { IS_NATIVE } from "./stripeFlow";

/**
 * Web only: open the new tab NOW, inside the tap — browsers block a tab
 * opened later, after the server has answered. It is pointed at the ID check
 * once the link arrives (openIdCheck), or closed if it doesn't.
 */
export function reserveIdCheckTab(): Window | null {
  if (IS_NATIVE) return null;
  try {
    const tab = window.open("", "_blank");
    // The ID check's pages must not be able to reach back into the app.
    if (tab) tab.opener = null;
    return tab;
  } catch {
    return null;
  }
}

/** Open the ID-check link: in-app browser (native), the reserved tab (web), or this tab as the last resort. */
export async function openIdCheck(url: string, tab: Window | null): Promise<"sheet" | "tab" | "page"> {
  if (IS_NATIVE) {
    try {
      await Browser.open({ url });
      return "sheet";
    } catch {
      // The sheet failed to present: fall through, never a dead button.
    }
  }
  if (tab && !tab.closed) {
    tab.location.href = url;
    return "tab";
  }
  window.location.href = url;
  return "page";
}

/**
 * Call `cb` when the talent is back from the ID check: native = they closed
 * the in-app browser; web = this tab is in front again. The caller
 * unsubscribes after the first call.
 */
export function onIdCheckReturn(cb: () => void): () => void {
  if (IS_NATIVE) {
    const sub = Browser.addListener("browserFinished", cb);
    return () => {
      sub.then((s) => s.remove()).catch(() => {});
    };
  }
  const back = () => {
    if (document.visibilityState === "visible") cb();
  };
  document.addEventListener("visibilitychange", back);
  window.addEventListener("focus", back);
  return () => {
    document.removeEventListener("visibilitychange", back);
    window.removeEventListener("focus", back);
  };
}
